import { useEffect, useMemo, useState } from 'react';
import {
  ArrowUpRight,
  CheckCheck,
  ClipboardList,
  Copy,
  Link2,
  ListTodo,
  Mail,
  Plus,
  ShieldCheck,
  UserPlus,
  Users,
} from 'lucide-react';
import { usePagination } from '../../hooks/usePagination';
import { cn } from '../../lib/cn';
import { formatRecurrence } from '../../lib/recurrence';
import Pagination from '../Pagination';
import AppAvatar from '../ui/AppAvatar';
import CollectionIcon from '../ui/CollectionIcon';
import RecurrenceEditor from '../ui/RecurrenceEditor';
import SegmentedTabs from '../ui/SegmentedTabs';
import {
  EmptyState,
  inputClassName,
  MetricCard,
  PageHero,
  SectionCard,
  SectionTitle,
  StatusPill,
} from '../ui/ScreenPrimitives';

const inviteFilterOptions = ['pending', 'accepted', 'revoked', 'expired'];

const familyTabItems = [
  { value: 'overview', label: 'Overview' },
  { value: 'members', label: 'Members' },
  { value: 'tasks', label: 'Tasks' },
  { value: 'shared_lists', label: 'Lists' },
  { value: 'invites', label: 'Invites' },
];

const primaryButtonClassName =
  'inline-flex items-center gap-2 rounded-xl bg-app-accent px-4 py-3 text-sm font-semibold text-white transition hover:bg-app-accent-strong disabled:cursor-not-allowed disabled:opacity-60';
const secondaryButtonClassName =
  'inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-4 py-3 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt disabled:cursor-not-allowed disabled:opacity-60';
const dangerButtonClassName =
  'inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60';

function toTitleCase(value) {
  if (!value) {
    return '';
  }

  return String(value)
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatDate(value) {
  if (!value) {
    return 'Unknown';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function toneForType(type) {
  if (type === 'warning') {
    return 'warning';
  }

  if (type === 'loading') {
    return 'loading';
  }

  if (type === 'success') {
    return 'success';
  }

  if (type === 'error') {
    return 'error';
  }

  return 'neutral';
}

function toneForRole(role) {
  if (role === 'owner') {
    return 'accent';
  }

  if (role === 'admin') {
    return 'family';
  }

  return 'neutral';
}

function toneForTaskPriority(priority) {
  if (priority === 'urgent') {
    return 'error';
  }

  if (priority === 'high') {
    return 'warning';
  }

  if (priority === 'medium') {
    return 'accent';
  }

  return 'neutral';
}

function toneForTaskStatus(status) {
  if (status === 'done') {
    return 'success';
  }

  if (status === 'in_progress') {
    return 'accent';
  }

  if (status === 'cancelled') {
    return 'error';
  }

  return 'neutral';
}

function toneForInviteStatus(status) {
  if (status === 'accepted') {
    return 'success';
  }

  if (status === 'revoked' || status === 'expired') {
    return 'error';
  }

  return 'warning';
}

function getMemberId(member, index) {
  return (
    member?._id ||
    member?.id ||
    member?.membershipId ||
    member?.userId ||
    member?.user?.userId ||
    `member-${index}`
  );
}

function getInviteId(invite, index) {
  return invite?._id || invite?.id || invite?.inviteId || `invite-${index}`;
}

function getIncomingInviteId(invite, index) {
  return invite?._id || invite?.id || invite?.inviteId || `incoming-invite-${index}`;
}

function getTaskId(task, index) {
  return task?._id || task?.id || task?.taskId || `family-task-${index}`;
}

function getFamilyListId(list, index) {
  return list?._id || list?.id || list?.listId || `family-list-${index}`;
}

function getMemberUserId(member, currentUser) {
  return (
    member?.userId ||
    member?.user?.userId ||
    member?.firebaseUid ||
    member?.user?.firebaseUid ||
    (member?.user?.email && member.user.email === currentUser?.email
      ? currentUser?.userId || currentUser?.firebaseUid || ''
      : '')
  );
}

function isEmail(value) {
  return Boolean(value && String(value).includes('@'));
}

function displayableName(value) {
  if (!value || isEmail(value)) {
    return '';
  }

  return value;
}

function isCurrentMember(member, currentUser) {
  const currentUserId = currentUser?.userId || currentUser?.firebaseUid;

  return Boolean(currentUserId && getMemberUserId(member, currentUser) === currentUserId);
}

function getMemberName(member, currentUser) {
  if (isCurrentMember(member, currentUser)) {
    return displayableName(currentUser?.name) || currentUser?.email || 'You';
  }

  return (
    displayableName(member?.user?.name) ||
    displayableName(member?.name) ||
    member?.user?.email ||
    member?.email ||
    'Family member'
  );
}

function getMemberEmail(member, currentUser) {
  if (isCurrentMember(member, currentUser)) {
    return currentUser?.email || 'Unavailable';
  }

  return member?.user?.email || member?.email || 'Unavailable';
}

function getIncomingInviteFamilyName(invite) {
  return (
    invite?.family?.name ||
    invite?.familyName ||
    invite?.familyDisplayName ||
    invite?.familyId ||
    'Family invite'
  );
}

function getInviteEmail(invite) {
  return invite?.email || invite?.recipientEmail || invite?.recipient?.email || 'Unknown';
}

function getFamilyTaskAssignee(task, familyMembers, currentUser) {
  const assigneeId = task?.assignedTo;
  if (!assigneeId) {
    return 'Unassigned';
  }

  const member = familyMembers.find(
    (candidate) => getMemberUserId(candidate, currentUser) === assigneeId
  );
  return member ? getMemberName(member, currentUser) : 'Family member';
}

function SharedListCard({ list, onOpenSharedList, index }) {
  return (
    <button
      className="group flex h-full flex-col rounded-[24px] border border-app-border/80 bg-app-surface/95 p-6 text-left shadow-subtle transition hover:-translate-y-1 hover:border-app-accent/25 hover:shadow-panel lg:p-7"
      type="button"
      onClick={() => onOpenSharedList(getFamilyListId(list, index))}
    >
      <div className="flex items-start justify-between gap-4">
        <div
          className="inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-subtle"
          style={{ backgroundColor: list?.color || '#60a5fa' }}
        >
          <CollectionIcon icon={list?.icon} size="lg" />
        </div>
        <StatusPill tone="family">Family shared</StatusPill>
      </div>

      <p className="mt-5 text-xl font-semibold tracking-tight text-app-ink">
        {list?.name || 'Untitled list'}
      </p>
      <p className="mt-2 text-sm leading-6 text-app-muted">
        {toTitleCase(list?.type || 'custom')}
      </p>
    </button>
  );
}

function FamilyView({
  currentUser,
  familyStatus,
  createFamilyStatus,
  inviteStatus,
  incomingInvitesStatus,
  acceptInviteStatus,
  familyTasksStatus,
  familySharedListsStatus,
  family,
  membership,
  familyRequestId,
  familyMembers,
  familyInvites,
  incomingFamilyInvites,
  familyTasks,
  familySharedLists,
  pendingInviteCount,
  familyActiveTab,
  familyInviteFilter,
  familyTaskFilter,
  focusMemberUserId,
  focusInviteId,
  showJoinPanel,
  createFamilyForm,
  createInviteForm,
  acceptInviteForm,
  familyTaskForm,
  createFamilyPending,
  createInvitePending,
  acceptInvitePending,
  acceptingIncomingInviteId,
  revokingInviteId,
  lastCreatedInviteToken,
  onCreateFamilyFormChange,
  onCreateInviteFormChange,
  onAcceptInviteFormChange,
  onFamilyTaskFormChange,
  onCreateFamily,
  onCreateInvite,
  onAcceptInvite,
  onAcceptIncomingInvite,
  onRevokeInvite,
  onFamilyTabChange,
  onFamilyInviteFilterChange,
  onFamilyTaskFilterChange,
  onCreateFamilyTask,
  onUpdateFamilyTask,
  onCompleteFamilyTask,
  onOpenSharedList,
  onOpenSharedListEditor,
  onToggleJoinPanel,
}) {
  const [copyStatus, setCopyStatus] = useState({
    type: 'idle',
    message: '',
  });

  const visibleInvites = useMemo(() => {
    if (!familyInviteFilter) {
      return familyInvites;
    }

    return familyInvites.filter((invite) => (invite?.status || 'pending') === familyInviteFilter);
  }, [familyInviteFilter, familyInvites]);

  const membersPagination = usePagination(familyMembers, 12);
  const tasksPagination = usePagination(familyTasks, 12);
  const sharedListsPagination = usePagination(familySharedLists, 8);
  const invitesPagination = usePagination(visibleInvites, 12);

  useEffect(() => {
    membersPagination.goToFirstPage();
  }, [familyMembers, membersPagination]);

  useEffect(() => {
    tasksPagination.goToFirstPage();
  }, [familyTasks, tasksPagination]);

  useEffect(() => {
    sharedListsPagination.goToFirstPage();
  }, [familySharedLists, sharedListsPagination]);

  useEffect(() => {
    invitesPagination.goToFirstPage();
  }, [visibleInvites, invitesPagination]);

  useEffect(() => {
    setCopyStatus({
      type: 'idle',
      message: '',
    });
  }, [lastCreatedInviteToken]);

  const canManageInvites = membership?.role === 'owner' || membership?.role === 'admin';
  const completedTaskCount = familyTasks.filter((task) => task?.status === 'done').length;

  async function handleCopyInviteToken() {
    if (!lastCreatedInviteToken) {
      return;
    }

    try {
      if (!navigator?.clipboard?.writeText) {
        throw new Error('Clipboard access is not available in this browser.');
      }

      await navigator.clipboard.writeText(lastCreatedInviteToken);
      setCopyStatus({
        type: 'success',
        message: 'Invite token copied.',
      });
    } catch (error) {
      setCopyStatus({
        type: 'error',
        message: error.message || 'Could not copy the invite token.',
      });
    }
  }

  if (!family) {
    return (
      <section className="w-full max-w-[1380px] text-app-ink">
        <PageHero
          eyebrow="Family"
          title="Start or join a family"
          lede="Create a shared family space, accept incoming invites, or join with a token. The actions and payloads remain the same; this pass only updates the presentation."
          bearyMessage={familyStatus.type === 'loading' ? 'Beary is checking your family space.' : undefined}
          bearyPreset="loading"
          action={<StatusPill tone={toneForType(familyStatus.type)}>{familyStatus.message}</StatusPill>}
        />

        <div className="mt-8 grid gap-8 xl:grid-cols-[minmax(0,1fr)_360px]">
          <SectionCard className="bg-app-shell">
            <SectionTitle
              title="Create family"
              copy="Start the shared space that members, tasks, and lists will attach to."
            />

            <form className="mt-5 space-y-4" onSubmit={onCreateFamily}>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="family-name">
                  Family name
                </label>
                <input
                  id="family-name"
                  value={createFamilyForm.name}
                  onChange={(event) => onCreateFamilyFormChange('name', event.target.value)}
                  placeholder="Chandak family"
                  disabled={createFamilyPending}
                  className={inputClassName}
                />
              </div>

              <button className={primaryButtonClassName} type="submit" disabled={createFamilyPending}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                <span>{createFamilyPending ? 'Creating family...' : 'Create family'}</span>
              </button>
            </form>

            {createFamilyStatus.message ? (
              <div className="mt-5">
                <StatusPill tone={toneForType(createFamilyStatus.type)}>
                  {createFamilyStatus.message}
                </StatusPill>
              </div>
            ) : null}
          </SectionCard>

          <aside className="space-y-8">
            <SectionCard>
              <SectionTitle
                title="Incoming invites"
                copy="Accept a family invite directly when one is available for this user."
              />

              {incomingInvitesStatus.message ? (
                <div className="mt-5">
                  <StatusPill tone={toneForType(incomingInvitesStatus.type)}>
                    {incomingInvitesStatus.message}
                  </StatusPill>
                </div>
              ) : null}

              {incomingFamilyInvites.length ? (
                <div className="mt-5 space-y-4">
                  {incomingFamilyInvites.map((invite, index) => {
                    const inviteId = getIncomingInviteId(invite, index);
                    const isFocusedInvite = focusInviteId && inviteId === focusInviteId;
                    return (
                      <article
                        className={cn(
                          'rounded-[22px] border p-5 shadow-subtle',
                          isFocusedInvite
                            ? 'border-app-accent/35 bg-blue-50/70 shadow-panel'
                            : 'border-app-border/80 bg-app-surface/95'
                        )}
                        key={inviteId}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="m-0 text-sm font-semibold text-app-ink">
                              {getIncomingInviteFamilyName(invite)}
                            </p>
                            <p className="mt-2 text-sm leading-6 text-app-muted">
                              Role: {toTitleCase(invite?.role || 'member')}
                            </p>
                          </div>
                          <StatusPill tone={toneForInviteStatus(invite?.status || 'pending')}>
                            {toTitleCase(invite?.status || 'pending')}
                          </StatusPill>
                        </div>
                        <p className="mt-2 text-sm leading-6 text-app-muted">
                          Expires {formatDate(invite?.expiresAt)}
                        </p>
                        <div className="mt-4">
                          <button
                            className={primaryButtonClassName}
                            type="button"
                            onClick={() => onAcceptIncomingInvite(inviteId)}
                            disabled={acceptingIncomingInviteId === inviteId}
                          >
                            <CheckCheck className="h-4 w-4" aria-hidden="true" />
                            <span>
                              {acceptingIncomingInviteId === inviteId ? 'Accepting...' : 'Accept invite'}
                            </span>
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-5">
                  <EmptyState
                    title="No incoming invites"
                    copy="If someone invited this account, it will appear here automatically."
                  />
                </div>
              )}
            </SectionCard>

            <SectionCard>
              <SectionTitle
                title="Join with token"
                copy="Use an invite token if someone has already created the family."
              />

              {!showJoinPanel ? (
                <div className="mt-5">
                  <EmptyState
                    title="Have an invite token?"
                    copy="Paste it here and join without leaving the family screen."
                    action={
                      <button className={secondaryButtonClassName} type="button" onClick={onToggleJoinPanel}>
                        <Link2 className="h-4 w-4" aria-hidden="true" />
                        <span>Join with invite</span>
                      </button>
                    }
                  />
                </div>
              ) : (
                <form className="mt-5 space-y-4" onSubmit={onAcceptInvite}>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-app-ink" htmlFor="family-invite-token">
                      Invite token
                    </label>
                    <textarea
                      id="family-invite-token"
                      rows="4"
                      value={acceptInviteForm.token}
                      onChange={(event) => onAcceptInviteFormChange('token', event.target.value)}
                      placeholder="Paste invite token"
                      disabled={acceptInvitePending}
                      className={inputClassName}
                    />
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button className={primaryButtonClassName} type="submit" disabled={acceptInvitePending}>
                      <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                      <span>{acceptInvitePending ? 'Joining family...' : 'Accept invite'}</span>
                    </button>
                    <button className={secondaryButtonClassName} type="button" onClick={onToggleJoinPanel}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {acceptInviteStatus.message ? (
                <div className="mt-5">
                  <StatusPill tone={toneForType(acceptInviteStatus.type)}>
                    {acceptInviteStatus.message}
                  </StatusPill>
                </div>
              ) : null}
            </SectionCard>
          </aside>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full max-w-[1380px] text-app-ink">
      <PageHero
        eyebrow="Family"
        title={family?.name || 'Family home'}
        lede="Shared household overview across membership, tasks, lists, and invite flow."
        bearyMessage={familyStatus.type === 'loading' ? 'Beary is checking your family space.' : undefined}
        bearyPreset="loading"
        action={<StatusPill tone={toneForType(familyStatus.type)}>{familyStatus.message}</StatusPill>}
      />

      <div className="mt-8 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Members" value={familyMembers.length} icon={Users} />
        <MetricCard
          label="Open tasks"
          value={familyTasks.length - completedTaskCount}
          icon={ClipboardList}
          accentClassName="text-blue-700"
        />
        <MetricCard
          label="Shared lists"
          value={familySharedLists.length}
          icon={ListTodo}
          accentClassName="text-emerald-700"
        />
        <MetricCard
          label="Pending invites"
          value={pendingInviteCount}
          icon={UserPlus}
          accentClassName="text-orange-700"
        />
      </div>

      <div className="mt-8">
        <SegmentedTabs
          value={familyActiveTab}
          onValueChange={onFamilyTabChange}
          ariaLabel="Family sections"
          items={familyTabItems}
          listClassName="w-full flex-wrap justify-start"
        />
      </div>

      {familyActiveTab === 'overview' ? (
        <div className="mt-8 grid gap-8 xl:grid-cols-[minmax(0,1fr)_340px]">
          <SectionCard>
            <SectionTitle
              title="Workspace overview"
              copy="See the collaborative state of this family at a glance."
            />
            <div className="mt-6 grid gap-5 md:grid-cols-2">
              <div className="rounded-[22px] border border-app-border/70 bg-app-surface/95 p-6 shadow-subtle">
                <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                  Role
                </p>
                <p className="mt-2 text-xl font-semibold text-app-ink">
                  {toTitleCase(membership?.role || 'member')}
                </p>
              </div>
              <div className="rounded-[22px] border border-app-border/70 bg-app-surface/95 p-6 shadow-subtle">
                <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                  Request ID
                </p>
                <p className="mt-2 break-all text-sm font-medium text-app-ink">
                  {familyRequestId || 'Unavailable'}
                </p>
              </div>
            </div>
          </SectionCard>

          <aside className="space-y-8">
            <SectionCard>
              <SectionTitle
                title="Shortcuts"
                copy="Jump directly into the collaborative areas."
              />
              <div className="mt-6 flex flex-col gap-3">
                <button className={secondaryButtonClassName} type="button" onClick={() => onFamilyTabChange('tasks')}>
                  <ClipboardList className="h-4 w-4" aria-hidden="true" />
                  <span>Open family tasks</span>
                </button>
                <button
                  className={secondaryButtonClassName}
                  type="button"
                  onClick={() => onFamilyTabChange('shared_lists')}
                >
                  <ListTodo className="h-4 w-4" aria-hidden="true" />
                  <span>Open shared lists</span>
                </button>
                <button className={secondaryButtonClassName} type="button" onClick={() => onFamilyTabChange('invites')}>
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  <span>Open invites</span>
                </button>
              </div>
            </SectionCard>

            <SectionCard className="bg-app-shell">
              <SectionTitle
                title="Your membership"
                copy="Current account and permission context."
              />
              <div className="mt-5 space-y-4">
                <div className="rounded-2xl border border-app-border/70 bg-app-surface/85 p-4">
                  <div className="flex items-center gap-3">
                    <AppAvatar
                      name={currentUser?.name}
                      email={currentUser?.email}
                      size="md"
                    />
                    <div>
                      <p className="m-0 text-sm font-semibold text-app-ink">
                        {currentUser?.name || currentUser?.email || 'Signed in'}
                      </p>
                      <p className="mt-1 text-sm text-app-muted">{currentUser?.email || 'Unknown'}</p>
                    </div>
                  </div>
                </div>
                <StatusPill tone={toneForRole(membership?.role)}>{toTitleCase(membership?.role || 'member')}</StatusPill>
                <div className="rounded-2xl border border-app-border/70 bg-app-surface/85 p-4">
                  <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                    Shared lists status
                  </p>
                  <p className="mt-2 text-sm font-medium text-app-ink">
                    {familySharedListsStatus.message}
                  </p>
                </div>
              </div>
            </SectionCard>
          </aside>
        </div>
      ) : null}

      {familyActiveTab === 'members' ? (
        <div className="mt-8 grid gap-8 xl:grid-cols-[minmax(0,1fr)_340px]">
          <SectionCard>
            <SectionTitle
              title="Members"
              copy="Current family membership and role distribution."
              action={<StatusPill tone="neutral">{membersPagination.totalItems} loaded</StatusPill>}
            />

            {membersPagination.totalItems ? (
              <div className="mt-5">
                <div className="grid gap-5 md:grid-cols-2">
                  {membersPagination.paginatedItems.map((member, index) => (
                    <article
                      className={cn(
                        'rounded-[24px] border p-6 shadow-subtle lg:p-7',
                        focusMemberUserId &&
                          getMemberUserId(member, currentUser) === focusMemberUserId
                          ? 'border-app-accent/35 bg-blue-50/70 shadow-panel'
                          : 'border-app-border/80 bg-app-surface/95'
                      )}
                      key={getMemberId(member, index)}
                    >
                      <div className="flex items-start gap-4">
                        <AppAvatar
                          name={getMemberName(member, currentUser)}
                          email={getMemberEmail(member, currentUser)}
                          size="lg"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="m-0 text-lg font-semibold text-app-ink">
                              {getMemberName(member, currentUser)}
                            </p>
                            <StatusPill tone={toneForRole(member?.role || membership?.role)}>
                              {toTitleCase(member?.role || 'member')}
                            </StatusPill>
                          </div>
                          <p className="mt-2 break-all text-sm leading-6 text-app-muted">
                            {getMemberEmail(member, currentUser)}
                          </p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
                <Pagination
                  currentPage={membersPagination.currentPage}
                  totalPages={membersPagination.totalPages}
                  hasNextPage={membersPagination.hasNextPage}
                  hasPreviousPage={membersPagination.hasPreviousPage}
                  onGoToPage={membersPagination.goToPage}
                  onGoToNext={membersPagination.goToNextPage}
                  onGoToPrevious={membersPagination.goToPreviousPage}
                  onGoToFirst={membersPagination.goToFirstPage}
                  onGoToLast={membersPagination.goToLastPage}
                  startIndex={membersPagination.startIndex}
                  endIndex={membersPagination.endIndex}
                  totalItems={membersPagination.totalItems}
                />
              </div>
            ) : (
              <div className="mt-5">
                <EmptyState
                  title="No family members loaded"
                  copy="Members will appear here once the workspace is fully available."
                />
              </div>
            )}
          </SectionCard>

          <aside className="space-y-8">
            {canManageInvites ? (
              <SectionCard>
                <SectionTitle
                  title="Invite member"
                  copy="Only owners and admins can send family invites."
                />
                <form className="mt-5 space-y-4" onSubmit={onCreateInvite}>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-app-ink" htmlFor="invite-email">
                      Email
                    </label>
                    <input
                      id="invite-email"
                      type="email"
                      value={createInviteForm.email}
                      onChange={(event) => onCreateInviteFormChange('email', event.target.value)}
                      placeholder="family@example.com"
                      disabled={createInvitePending}
                      className={inputClassName}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-app-ink" htmlFor="invite-role">
                      Role
                    </label>
                    <select
                      id="invite-role"
                      value={createInviteForm.role}
                      onChange={(event) => onCreateInviteFormChange('role', event.target.value)}
                      disabled={createInvitePending}
                      className={inputClassName}
                    >
                      <option value="member">member</option>
                      <option value="admin">admin</option>
                    </select>
                  </div>

                  <button className={primaryButtonClassName} type="submit" disabled={createInvitePending}>
                    <UserPlus className="h-4 w-4" aria-hidden="true" />
                    <span>{createInvitePending ? 'Sending invite...' : 'Send invite'}</span>
                  </button>
                </form>

                {inviteStatus.message ? (
                  <div className="mt-5">
                    <StatusPill tone={toneForType(inviteStatus.type)}>{inviteStatus.message}</StatusPill>
                  </div>
                ) : null}
              </SectionCard>
            ) : (
              <SectionCard>
                <SectionTitle
                  title="Invite controls unavailable"
                  copy="Only owners and admins can create or revoke invites."
                />
                <div className="mt-5">
                  <StatusPill tone="neutral">Read only</StatusPill>
                </div>
              </SectionCard>
            )}

            {lastCreatedInviteToken ? (
              <SectionCard className="bg-app-shell">
                <SectionTitle
                  title="Share token now"
                  copy="This token may not be recoverable later."
                />
                <div className="mt-5 rounded-[22px] border border-app-border/70 bg-app-surface/95 p-4">
                  <p className="m-0 break-all text-sm font-medium text-app-ink">
                    {lastCreatedInviteToken}
                  </p>
                </div>
                <div className="mt-4">
                  <button className={secondaryButtonClassName} type="button" onClick={handleCopyInviteToken}>
                    <Copy className="h-4 w-4" aria-hidden="true" />
                    <span>Copy token</span>
                  </button>
                </div>
                {copyStatus.message ? (
                  <div className="mt-4">
                    <StatusPill tone={toneForType(copyStatus.type)}>{copyStatus.message}</StatusPill>
                  </div>
                ) : null}
              </SectionCard>
            ) : null}
          </aside>
        </div>
      ) : null}

      {familyActiveTab === 'tasks' ? (
        <div className="mt-8 grid gap-8 xl:grid-cols-[340px_minmax(0,1fr)]">
          <aside className="space-y-8">
            <SectionCard>
              <SectionTitle
                title="Filters"
                copy="Keep the current family task filter semantics while using the new control style."
                action={<StatusPill tone={toneForType(familyTasksStatus.type)}>{familyTasksStatus.message}</StatusPill>}
              />

              <div className="mt-6 space-y-5">
                <SegmentedTabs
                  value={familyTaskFilter}
                  onValueChange={onFamilyTaskFilterChange}
                  ariaLabel="Family task filter"
                  items={[
                    { value: 'open', label: 'Open' },
                    { value: 'mine', label: 'Mine' },
                    { value: 'done', label: 'Done' },
                    { value: 'all', label: 'All' },
                  ]}
                  listClassName="w-full flex-wrap justify-start"
                />
              </div>
            </SectionCard>

            <SectionCard>
              <SectionTitle
                title="Create task"
                copy="Add the next shared responsibility for the family."
              />

              <form className="mt-6 space-y-5" onSubmit={onCreateFamilyTask}>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-app-ink" htmlFor="family-task-title">
                    Title
                  </label>
                  <input
                    id="family-task-title"
                    value={familyTaskForm.title}
                    onChange={(event) => onFamilyTaskFormChange('title', event.target.value)}
                    placeholder="Review school forms"
                    className={inputClassName}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-app-ink" htmlFor="family-task-description">
                    Description
                  </label>
                  <textarea
                    id="family-task-description"
                    rows="3"
                    value={familyTaskForm.description}
                    onChange={(event) => onFamilyTaskFormChange('description', event.target.value)}
                    placeholder="Before Monday"
                    className={inputClassName}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-app-ink" htmlFor="family-task-assigned-to">
                    Assigned to
                  </label>
                  <select
                    id="family-task-assigned-to"
                    value={familyTaskForm.assignedTo}
                    onChange={(event) => onFamilyTaskFormChange('assignedTo', event.target.value)}
                    className={inputClassName}
                  >
                    <option value="">Unassigned</option>
                    {familyMembers.map((member, index) => {
                      const memberUserId = getMemberUserId(member, currentUser);

                      return (
                        <option
                          key={getMemberId(member, index)}
                          value={memberUserId}
                          disabled={!memberUserId}
                        >
                          {getMemberName(member, currentUser)}
                          {!memberUserId ? ' (unavailable)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-app-ink" htmlFor="family-task-priority">
                      Priority
                    </label>
                    <select
                      id="family-task-priority"
                      value={familyTaskForm.priority}
                      onChange={(event) => onFamilyTaskFormChange('priority', event.target.value)}
                      className={inputClassName}
                    >
                      <option value="low">low</option>
                      <option value="medium">medium</option>
                      <option value="high">high</option>
                      <option value="urgent">urgent</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-app-ink" htmlFor="family-task-due-date">
                      Due date
                    </label>
                    <input
                      id="family-task-due-date"
                      type="datetime-local"
                      value={familyTaskForm.dueDate}
                      onChange={(event) => onFamilyTaskFormChange('dueDate', event.target.value)}
                      className={inputClassName}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-app-ink">Recurrence</label>
                  <RecurrenceEditor
                    value={familyTaskForm.recurrence}
                    onChange={(value) => onFamilyTaskFormChange('recurrence', value)}
                    idPrefix="family-task-recurrence"
                    requireDateCopy="A recurring family task needs a due date. Completing it advances to the next occurrence."
                  />
                </div>

                <button className={primaryButtonClassName} type="submit">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  <span>Create family task</span>
                </button>
              </form>
            </SectionCard>
          </aside>

          <SectionCard>
            <SectionTitle
              title="Family tasks"
              copy="Shared household work aligned to active family members."
              action={<StatusPill tone="neutral">{tasksPagination.totalItems} loaded</StatusPill>}
            />

            {tasksPagination.totalItems ? (
              <div className="mt-6">
                <div className="grid gap-5 md:grid-cols-2">
                  {tasksPagination.paginatedItems.map((task, index) => {
                    const taskId = getTaskId(task, index);
                    const recurrenceLabel = formatRecurrence(task?.recurrence);

                    return (
                      <article
                        className="rounded-[24px] border border-app-border/80 bg-app-surface/95 p-6 shadow-subtle lg:p-7"
                        key={taskId}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="m-0 text-lg font-semibold text-app-ink">
                              {task?.title || 'Untitled task'}
                            </p>
                            <p className="mt-2 text-sm leading-6 text-app-muted">
                              {task?.description || 'No description added yet.'}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <StatusPill tone={toneForTaskStatus(task?.status || 'open')}>
                              {toTitleCase(task?.status || 'open')}
                            </StatusPill>
                            <StatusPill tone={toneForTaskPriority(task?.priority || 'medium')}>
                              {task?.priority || 'medium'}
                            </StatusPill>
                          </div>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                          <StatusPill tone="neutral">
                            {getFamilyTaskAssignee(task, familyMembers, currentUser)}
                          </StatusPill>
                          <StatusPill tone="neutral">Due {formatDate(task?.dueDate)}</StatusPill>
                          {recurrenceLabel ? (
                            <StatusPill tone="neutral">{recurrenceLabel}</StatusPill>
                          ) : null}
                        </div>

                        <div className="mt-5 flex flex-wrap gap-3">
                          {task?.status !== 'in_progress' && task?.status !== 'done' ? (
                            <button
                              className={secondaryButtonClassName}
                              type="button"
                              onClick={() => onUpdateFamilyTask(taskId, { status: 'in_progress' })}
                            >
                              Start
                            </button>
                          ) : null}
                          {task?.status !== 'done' ? (
                            <button
                              className={primaryButtonClassName}
                              type="button"
                              onClick={() => onCompleteFamilyTask(taskId)}
                            >
                              <CheckCheck className="h-4 w-4" aria-hidden="true" />
                              <span>Complete task</span>
                            </button>
                          ) : null}
                        </div>
                      </article>
                    );
                  })}
                </div>
                <Pagination
                  currentPage={tasksPagination.currentPage}
                  totalPages={tasksPagination.totalPages}
                  hasNextPage={tasksPagination.hasNextPage}
                  hasPreviousPage={tasksPagination.hasPreviousPage}
                  onGoToPage={tasksPagination.goToPage}
                  onGoToNext={tasksPagination.goToNextPage}
                  onGoToPrevious={tasksPagination.goToPreviousPage}
                  onGoToFirst={tasksPagination.goToFirstPage}
                  onGoToLast={tasksPagination.goToLastPage}
                  startIndex={tasksPagination.startIndex}
                  endIndex={tasksPagination.endIndex}
                  totalItems={tasksPagination.totalItems}
                />
              </div>
            ) : (
              <div className="mt-5">
                <EmptyState
                  title="No family tasks in this view"
                  copy="Create the first task or switch the active filter."
                />
              </div>
            )}
          </SectionCard>
        </div>
      ) : null}

      {familyActiveTab === 'shared_lists' ? (
        <div className="mt-8 grid gap-8 xl:grid-cols-[minmax(0,1fr)_320px]">
          <SectionCard>
            <SectionTitle
              title="Shared lists"
              copy="Collections the whole household can edit together."
              action={<StatusPill tone="neutral">{sharedListsPagination.totalItems} loaded</StatusPill>}
            />

            {familySharedListsStatus.message ? (
              <div className="mt-5">
                <StatusPill tone={toneForType(familySharedListsStatus.type)}>
                  {familySharedListsStatus.message}
                </StatusPill>
              </div>
            ) : null}

            {sharedListsPagination.totalItems ? (
              <div className="mt-6">
                <div className="grid gap-5 md:grid-cols-2">
                  {sharedListsPagination.paginatedItems.map((list, index) => (
                    <SharedListCard
                      key={getFamilyListId(list, index)}
                      list={list}
                      index={index}
                      onOpenSharedList={onOpenSharedList}
                    />
                  ))}
                </div>
                <Pagination
                  currentPage={sharedListsPagination.currentPage}
                  totalPages={sharedListsPagination.totalPages}
                  hasNextPage={sharedListsPagination.hasNextPage}
                  hasPreviousPage={sharedListsPagination.hasPreviousPage}
                  onGoToPage={sharedListsPagination.goToPage}
                  onGoToNext={sharedListsPagination.goToNextPage}
                  onGoToPrevious={sharedListsPagination.goToPreviousPage}
                  onGoToFirst={sharedListsPagination.goToFirstPage}
                  onGoToLast={sharedListsPagination.goToLastPage}
                  startIndex={sharedListsPagination.startIndex}
                  endIndex={sharedListsPagination.endIndex}
                  totalItems={sharedListsPagination.totalItems}
                />
              </div>
            ) : (
              <div className="mt-5">
                <EmptyState
                  title="No shared lists yet"
                  copy="Create the first shared collection for shopping, travel, or household work."
                />
              </div>
            )}
          </SectionCard>

          <aside className="space-y-8">
            <SectionCard className="bg-app-shell">
              <SectionTitle
                title="Create shared list"
                copy="Open the full-screen editor with Family Shared preselected."
              />
              <div className="mt-5">
                <button className={primaryButtonClassName} type="button" onClick={onOpenSharedListEditor}>
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  <span>New shared list</span>
                </button>
              </div>
            </SectionCard>
          </aside>
        </div>
      ) : null}

      {familyActiveTab === 'invites' ? (
        <div className="mt-8 grid gap-8 xl:grid-cols-[minmax(0,1fr)_320px]">
          <SectionCard>
            <SectionTitle
              title="Invites"
              copy="Filter invite history and revoke pending tokens when needed."
              action={<StatusPill tone="neutral">{invitesPagination.totalItems} loaded</StatusPill>}
            />

            <div className="mt-6">
              <SegmentedTabs
                value={familyInviteFilter}
                onValueChange={onFamilyInviteFilterChange}
                ariaLabel="Invite filters"
                items={inviteFilterOptions.map((filter) => ({
                  value: filter,
                  label: toTitleCase(filter),
                }))}
                listClassName="w-full flex-wrap justify-start"
              />
            </div>

            {inviteStatus.message ? (
              <div className="mt-5">
                <StatusPill tone={toneForType(inviteStatus.type)}>{inviteStatus.message}</StatusPill>
              </div>
            ) : null}

            {invitesPagination.totalItems ? (
              <div className="mt-6">
                <div className="space-y-5">
                  {invitesPagination.paginatedItems.map((invite, index) => {
                    const inviteId = getInviteId(invite, index);
                    const isFocusedInvite = focusInviteId && inviteId === focusInviteId;

                    return (
                      <article
                        className={cn(
                          'rounded-[24px] border p-6 shadow-subtle lg:p-7',
                          isFocusedInvite
                            ? 'border-app-accent/35 bg-blue-50/70 shadow-panel'
                            : 'border-app-border/80 bg-app-surface/95'
                        )}
                        key={inviteId}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="m-0 text-lg font-semibold text-app-ink">
                              {getInviteEmail(invite)}
                            </p>
                            <p className="mt-2 text-sm leading-6 text-app-muted">
                              Role: {toTitleCase(invite?.role || 'member')}
                            </p>
                          </div>
                          <StatusPill tone={toneForInviteStatus(invite?.status || 'pending')}>
                            {toTitleCase(invite?.status || 'pending')}
                          </StatusPill>
                        </div>

                        <div className="mt-4 grid gap-3 sm:grid-cols-2">
                          <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                            <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                              Created
                            </p>
                            <p className="mt-2 text-sm font-medium text-app-ink">
                              {formatDate(invite?.createdAt)}
                            </p>
                          </div>
                          <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                            <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                              Updated
                            </p>
                            <p className="mt-2 text-sm font-medium text-app-ink">
                              {formatDate(invite?.updatedAt)}
                            </p>
                          </div>
                        </div>

                        {invite?.status === 'pending' && canManageInvites ? (
                          <div className="mt-4">
                            <button
                              className={dangerButtonClassName}
                              type="button"
                              onClick={() => onRevokeInvite(inviteId)}
                              disabled={revokingInviteId === inviteId}
                            >
                              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                              <span>{revokingInviteId === inviteId ? 'Revoking...' : 'Revoke invite'}</span>
                            </button>
                          </div>
                        ) : null}
                      </article>
                    );
                  })}
                </div>
                <Pagination
                  currentPage={invitesPagination.currentPage}
                  totalPages={invitesPagination.totalPages}
                  hasNextPage={invitesPagination.hasNextPage}
                  hasPreviousPage={invitesPagination.hasPreviousPage}
                  onGoToPage={invitesPagination.goToPage}
                  onGoToNext={invitesPagination.goToNextPage}
                  onGoToPrevious={invitesPagination.goToPreviousPage}
                  onGoToFirst={invitesPagination.goToFirstPage}
                  onGoToLast={invitesPagination.goToLastPage}
                  startIndex={invitesPagination.startIndex}
                  endIndex={invitesPagination.endIndex}
                  totalItems={invitesPagination.totalItems}
                />
              </div>
            ) : (
              <div className="mt-5">
                <EmptyState
                  title="No invites in this view"
                  copy="Switch filters or create a new invite from the Members tab."
                />
              </div>
            )}
          </SectionCard>

          <aside className="space-y-8">
            <SectionCard>
              <SectionTitle
                title="Invite management"
                copy="Invite state and revoke behavior remain unchanged."
              />
              <div className="mt-5 space-y-4">
                <StatusPill tone={canManageInvites ? 'family' : 'neutral'}>
                  {canManageInvites ? 'Admin controls enabled' : 'Read only'}
                </StatusPill>
                <p className="text-sm leading-6 text-app-muted">
                  Pending invite count: {pendingInviteCount}
                </p>
              </div>
            </SectionCard>

            {lastCreatedInviteToken ? (
              <SectionCard className="bg-app-shell">
                <SectionTitle
                  title="Latest token"
                  copy="Share or copy the latest invite token."
                />
                <div className="mt-5 rounded-[22px] border border-app-border/70 bg-app-surface/95 p-4">
                  <p className="m-0 break-all text-sm font-medium text-app-ink">
                    {lastCreatedInviteToken}
                  </p>
                </div>
                <div className="mt-4">
                  <button className={secondaryButtonClassName} type="button" onClick={handleCopyInviteToken}>
                    <Copy className="h-4 w-4" aria-hidden="true" />
                    <span>Copy token</span>
                  </button>
                </div>
                {copyStatus.message ? (
                  <div className="mt-4">
                    <StatusPill tone={toneForType(copyStatus.type)}>{copyStatus.message}</StatusPill>
                  </div>
                ) : null}
              </SectionCard>
            ) : null}
          </aside>
        </div>
      ) : null}
    </section>
  );
}

export default FamilyView;
